<script>
import { prettyBytes } from '@/modules/prettifier';

export default {
  name: 'FileListItem',
  props: {
    busy: Boolean,
    disabled: Boolean,
    enabled: Boolean,
    hidden: Boolean,
    name: String,
    path: String,
    selected: Boolean,
    size: Number,
    type: String
  },
  computed: {
    icon() {
      if (this.busy) return 'el-icon-fa-circle-notch el-icon-fa-spin';
      if (this.type === 'directory' && this.name === '..') return 'el-icon-fa-fw el-icon-fa-arrow-left';
      if (this.type === 'directory') return 'el-icon-fa-folder';
      if (this.enabled) return 'el-icon-fa-file-check';
      if (this.name.match(/\.(gif|jpeg|jpg|png|svg|webp)$/i)) return 'el-icon-fa-fw el-icon-fa-file-image';
      if (this.name.match(/\.(pdf)$/i)) return 'el-icon-fa-fw el-icon-fa-file-pdf';
      if (this.name.match(/\.(doc|docx)$/i)) return 'el-icon-fa-fw el-icon-fa-file-word';
      if (this.name.match(/\.(ppt|pptx)$/i)) return 'el-icon-fa-fw el-icon-fa-file-powerpoint';
      if (this.name.match(/\.(xls|xlsx)$/i)) return 'el-icon-fa-fw el-icon-fa-file-excel';
      if (this.name.match(/\.(txt|md)$/i)) return 'el-icon-fa-fw el-icon-fa-file-alt';
      if (
        this.name.match(
          /\.(asp|aspx|ascx|cshtml|css|ctp|dwt|htm|html|inc|ixml|js|json|jps|lasso|master|md|phtml|php|py|rb|shtm|shtml|tpl|txt|xml)$/i
        )
      )
        return 'el-icon-fa-fw el-icon-fa-file-code';
      if (this.name.match(/\.(aac|m4a|m4p|mp3|ogg|wav)$/i)) return 'el-icon-fa-fw el-icon-fa-file-audio';
      if (this.name.match(/\.(avi|mov|m4v|mp2|mp4|mpeg|mpg|mpv|webm)$/i)) return 'el-icon-fa-fw el-icon-fa-file-video';
      return 'el-icon-fa-file';
    }
  },
  methods: {
    handleClick(event) {
      // Don't emit clicks for the parent directory
      if (this.type === 'directory' && this.name === '..') {
        return;
      }

      // Don't emit clicks for disabled files
      if (this.disabled) {
        return;
      }

      this.$emit('click', event);
    },

    handleDoubleClick(event) {
      this.$emit('dblclick', event);
    },

    prettyBytes(bytes) {
      return prettyBytes(bytes);
    }
  }
};
</script>

<template>
  <div
    :class="{
      'file-list-item': true,
      'is-directory': type === 'directory',
      'is-disabled': disabled,
      'is-enabled': enabled,
      'is-file': type === 'file',
      'is-hidden': hidden,
      'is-selected': selected
    }"
  >
    <!-- Directory -->
    <template v-if="type === 'directory'">
      <a class="directory-link" href="#" @click.prevent="$emit('path-change', path)">
        <span class="icon">
          <i :class="`el-icon-fa-fw ${icon}`" />
        </span>

        <span class="name">
          {{ name === '..' ? $t('Back') : name }}
        </span>
      </a>

      <a class="directory-selectable" href="#" @click.prevent="handleClick" @dblclick="handleDoubleClick" />
    </template>

    <!-- File -->
    <template v-else>
      <a class="file-selectable" href="#" @click.prevent="handleClick" @dblclick="handleDoubleClick">
        <span class="icon">
          <i :class="`el-icon-fa-fw ${icon}`" />
        </span>

        <span class="name">
          {{ name }}
        </span>

        <small v-if="type === 'file' && size" class="size">
          {{ prettyBytes(size) }}
        </small>
      </a>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.file-list-item {
  display: flex;
  transition: 0.1s background-color;
  white-space: nowrap;
  cursor: default;

  .directory-link {
    flex: 0 0 auto;
    padding: 0.35rem;
  }

  .directory-selectable {
    display: flex;
    width: 100%;
    flex: 1 1 auto;
    color: inherit;
    padding: 0.35rem;
    cursor: default;
  }

  .file-selectable {
    display: flex;
    width: 100%;
    flex: 1 1 auto;
    color: inherit;
    padding: 0.35rem;
    cursor: default;
  }

  .icon {
    flex: 0 0 1.5rem;
    color: $--color-gray-40;
    margin-right: 0.125rem;
  }

  .name {
    max-width: calc(100% - 5.5rem);
    flex: 1 1 auto;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .size {
    flex: 0 0 4rem;
    color: $--color-gray-40;
    text-align: right;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &.is-file.is-enabled {
    color: $--color-success;

    .icon,
    .size {
      color: inherit;
    }
  }

  &.is-file.is-disabled {
    color: lighten($--color-gray-30, 5%);

    .icon,
    .size {
      color: inherit;
    }
  }

  &.is-hidden {
    display: none;
  }

  &.is-selected {
    background-color: $--color-primary-light-9;
    color: $--color-primary;

    .icon,
    .size {
      color: inherit;
    }
  }
}
</style>
